"use client";

import {JSX, useEffect, useRef, useState} from "react";

import SmartImage from "@/shared/ui/smart-image";

import {useTranslation} from "@/shared/i18n";
import {ButtonBase} from "@/shared/ui/buttons/button-base";
import {Typography} from "@/shared/ui/data-display/typography";
import {assetPath} from "@/shared/utils/asset-path";

import "./styles/text-section.scss";

interface TextSectionProps {
    title: string;
    text: string;
    enableReadMore?: boolean;
    className?: string;
}

export function TextSection({title, text, enableReadMore = false, className = ""}: TextSectionProps): JSX.Element {
    const {t} = useTranslation("common");
    const [isExpanded, setIsExpanded] = useState(false);
    const contentRef = useRef<HTMLDivElement>(null);
    const COLLAPSED_HEIGHT = 250;
    const [maxHeight, setMaxHeight] = useState<number | undefined>(enableReadMore ? COLLAPSED_HEIGHT : undefined);

    useEffect(() => {
        if (contentRef.current && enableReadMore) {
            if (isExpanded) {
                setMaxHeight(contentRef.current.scrollHeight);
            } else {
                setMaxHeight(COLLAPSED_HEIGHT);
            }
        }
    }, [isExpanded, enableReadMore]);

    const handleToggle = (): void => {
        setIsExpanded(!isExpanded);
    };

    return (
        <div className={`text-section ${className}`}>
            <Typography as="h2" size={{desktop: 24, mobile: 20}} weight={600} className="text-section__title">
                {title}
            </Typography>

            <div
                ref={contentRef}
                className={`text-section__content ${enableReadMore && !isExpanded ? "text-section__content--collapsed" : ""}`}
                style={enableReadMore ? {maxHeight: maxHeight !== undefined ? `${maxHeight}px` : undefined} : undefined}
            >
                <Typography as="div" size={{desktop: 16, mobile: 14}} weight={400} className="text-section__text" innerHTML={text} />
            </div>

            {enableReadMore && (
                <ButtonBase variant="secondary-outline" onClick={handleToggle} className="text-section__button" size="sm">
                    {isExpanded ? t("readLess") : t("readMore")}
                    <SmartImage
                        src={assetPath("/images/icons/arrow-black-down.svg")}
                        alt="Arrow"
                        width={11}
                        height={7}
                        className={`text-section__arrow ${isExpanded ? "text-section__arrow--up" : ""}`}
                    />
                </ButtonBase>
            )}
        </div>
    );
}
